import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  FlaskConical,
  Loader2,
  RotateCcw,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebounced } from '@/hooks/useDebounced';
import { ordersService } from '@/services/orders';
import { customersService } from '@/services/customers';
import { inventoryService } from '@/services/inventory';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  testDataService,
  type CleanupRun,
  type TestDataEntityType,
} from '@/services/platform/test-data';

interface PickerRow {
  id: string;
  label: string;
  sublabel?: string;
}

/** Per-type searchable fetchers reusing the existing list APIs. Support
 *  tickets have no ERP list screen, so they fall back to by-ID entry. */
const PICKERS: Partial<Record<TestDataEntityType, { fetch: (q: string) => Promise<PickerRow[]> }>> = {
  SALES_ORDER: {
    fetch: async (q) =>
      (await ordersService.list({ search: q || undefined, pageSize: 50 })).rows.map((o) => ({
        id: o.id,
        label: o.orderNumber,
        sublabel: o.status,
      })),
  },
  CUSTOMER: {
    fetch: async (q) =>
      (await customersService.list({ search: q || undefined, pageSize: 50 })).items.map((c) => ({
        id: c.id,
        label: c.name,
        sublabel: c.code,
      })),
  },
  INVENTORY_ITEM: {
    fetch: async (q) =>
      (await inventoryService.list({ search: q || undefined, pageSize: 50 })).items.map((i) => ({
        id: i.id,
        label: i.name,
        sublabel: i.sku,
      })),
  },
};

const TYPE_OPTIONS: { value: TestDataEntityType; label: string }[] = [
  { value: 'SALES_ORDER', label: 'Sales orders' },
  { value: 'CUSTOMER', label: 'Customers' },
  { value: 'INVENTORY_ITEM', label: 'Items' },
  { value: 'SUPPORT_REQUEST', label: 'Support tickets' },
];

const TYPE_LABEL: Record<TestDataEntityType, string> = {
  SALES_ORDER: 'Order',
  CUSTOMER: 'Customer',
  INVENTORY_ITEM: 'Item',
  SUPPORT_REQUEST: 'Ticket',
};

const STATUS_VARIANT: Record<CleanupRun['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  RUNNING: 'secondary',
  COMPLETED: 'default',
  RESTORING: 'secondary',
  RESTORED: 'outline',
  FAILED: 'destructive',
};

function parseIds(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

function fmtDate(s?: string | null): string {
  return s ? new Date(s).toLocaleString() : '—';
}

/**
 * Settings → Test data.
 *
 * Mark driving entities (orders, customers, items, support tickets) as
 * test, preview exactly what a cleanup would remove, run it, and restore
 * any past run from its archive. The cleanup archives every affected row
 * before deleting — nothing is unrecoverable.
 */
export function TestDataPage() {
  const qc = useQueryClient();

  const markedQuery = useQuery({
    queryKey: ['test-data', 'marked'],
    queryFn: testDataService.listMarked,
  });
  const runsQuery = useQuery({
    queryKey: ['test-data', 'runs'],
    queryFn: testDataService.listRuns,
  });
  // Auto-preview whenever something is marked, so "Run cleanup" reflects
  // the live blast radius (and conflicts) without a manual Preview click.
  const previewQuery = useQuery({
    queryKey: ['test-data', 'preview'],
    queryFn: testDataService.preview,
    enabled: (markedQuery.data?.length ?? 0) > 0,
  });

  const [markType, setMarkType] = useState<TestDataEntityType>('SALES_ORDER');
  const [markIds, setMarkIds] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [cleanupNote, setCleanupNote] = useState('BETA Testing Cleanup Done!');
  const [restoreTarget, setRestoreTarget] = useState<CleanupRun | null>(null);
  const debouncedSearch = useDebounced(search);

  const hasPicker = markType !== 'SUPPORT_REQUEST';

  const changeType = (t: TestDataEntityType) => {
    setMarkType(t);
    setSelectedIds(new Set());
    setSearch('');
    setMarkIds('');
  };
  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const pickerQuery = useQuery({
    queryKey: ['test-data', 'picker', markType, debouncedSearch],
    queryFn: () => PICKERS[markType]!.fetch(debouncedSearch),
    enabled: hasPicker,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['test-data'] });
  };

  const markIdsToSend = hasPicker ? Array.from(selectedIds) : parseIds(markIds);

  const markMut = useMutation({
    mutationFn: () => testDataService.mark(markType, markIdsToSend),
    onSuccess: (r) => {
      toast.success(`Marked ${r.marked} ${TYPE_LABEL[markType].toLowerCase()}(s) as test`);
      setMarkIds('');
      setSelectedIds(new Set());
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unmarkMut = useMutation({
    mutationFn: (v: { entityType: TestDataEntityType; entityId: string }) =>
      testDataService.unmark(v.entityType, [v.entityId]),
    onSuccess: () => {
      toast.success('Unmarked');
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cleanupMut = useMutation({
    mutationFn: () => testDataService.cleanup(cleanupNote.trim()),
    onSuccess: (r) => {
      toast.success(`Cleanup complete — ${r.totalRows} rows archived + removed`);
      setConfirmCleanup(false);
      invalidate();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setConfirmCleanup(false);
    },
  });

  const restoreMut = useMutation({
    mutationFn: (runId: string) => testDataService.restore(runId),
    onSuccess: (r) => {
      toast.success(`Restored ${r.restoredRows} rows`);
      setRestoreTarget(null);
      invalidate();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setRestoreTarget(null);
    },
  });

  // One-click conflict resolution — mark the orders that reference the
  // test item so the whole chain comes into scope.
  const resolveMut = useMutation({
    mutationFn: (ids: string[]) => testDataService.mark('SALES_ORDER', ids),
    onSuccess: (r) => {
      toast.success(`Marked ${r.marked} referencing order(s) as test`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const marked = markedQuery.data ?? [];
  const runs = runsQuery.data ?? [];
  const pickerRows = pickerQuery.data ?? [];
  const selectedCount = markIdsToSend.length;
  const preview = previewQuery.data ?? null;

  // Why "Run cleanup" can't run yet — surfaced next to the button so a
  // disabled state is never unexplained.
  const cleanupBlockedReason =
    marked.length === 0
      ? 'Nothing is marked as test yet.'
      : previewQuery.isLoading
        ? 'Calculating what would be removed…'
        : preview && preview.conflicts.length > 0
          ? 'Resolve the conflicts below first (e.g. unmark the item, or also mark the orders that use it).'
          : preview && preview.totalRows === 0
            ? 'Nothing to remove for the current marks.'
            : null;
  const canRunCleanup = !cleanupBlockedReason && !!preview;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Settings"
        title="Test data"
        description="Flag test orders, customers, items and support tickets, then run an auditable cleanup. Every removed row is archived first, so any run can be fully restored."
      />

      <Tabs defaultValue="manage">
        <TabsList>
          <TabsTrigger value="manage">Mark &amp; clean</TabsTrigger>
          <TabsTrigger value="history">
            History{runs.length ? ` (${runs.length})` : ''}
          </TabsTrigger>
        </TabsList>

        {/* ── Mark & clean ────────────────────────────────────────── */}
        <TabsContent value="manage" className="space-y-5">
          {/* Mark */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FlaskConical className="h-4 w-4" /> Mark as test
              </CardTitle>
              <CardDescription>
                Pick a type, search, and select the records to flag. You can also mark in
                context from the Orders / Customers / Items screens. Marking a customer pulls
                in all their orders and related records at cleanup time.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="sm:w-52">
                  <Label className="mb-1.5 block text-xs">Type</Label>
                  <Select value={markType} onValueChange={(v) => changeType(v as TestDataEntityType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {hasPicker ? (
                  <div className="flex-1 space-y-2">
                    <Label className="mb-1.5 block text-xs">
                      Search &amp; select ({selectedCount} selected)
                    </Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        className="pl-8"
                        placeholder={`Search ${TYPE_LABEL[markType].toLowerCase()}s…`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <ScrollArea className="h-52 rounded-md border">
                      {pickerQuery.isLoading ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
                      ) : pickerRows.length === 0 ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          No matches.
                        </div>
                      ) : (
                        <ul className="divide-y">
                          {pickerRows.map((row) => {
                            const checked = selectedIds.has(row.id);
                            return (
                              <li key={row.id}>
                                <button
                                  type="button"
                                  onClick={() => toggleSelected(row.id)}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
                                >
                                  <span
                                    className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${
                                      checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                                    }`}
                                  >
                                    {checked && <Check className="h-3 w-3" />}
                                  </span>
                                  <span className="flex-1 truncate">{row.label}</span>
                                  {row.sublabel && (
                                    <span className="font-mono text-[11px] text-muted-foreground">
                                      {row.sublabel}
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </ScrollArea>
                  </div>
                ) : (
                  <div className="flex-1">
                    <Label className="mb-1.5 block text-xs">
                      Support ticket IDs (no list screen — paste IDs)
                    </Label>
                    <Textarea
                      rows={4}
                      placeholder="one UUID per line"
                      value={markIds}
                      onChange={(e) => setMarkIds(e.target.value)}
                      className="font-mono text-xs"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => markMut.mutate()}
                  disabled={selectedCount === 0 || markMut.isPending}
                >
                  {markMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Mark {selectedCount > 0 ? `${selectedCount} ` : ''}as test
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Currently marked */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Currently marked ({marked.length})</CardTitle>
              <CardDescription>These roots are in scope for the next cleanup.</CardDescription>
            </CardHeader>
            <CardContent>
              {markedQuery.isLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
              ) : marked.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Nothing marked yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Type</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="w-40">Marked</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {marked.map((m) => (
                      <TableRow key={m.markId}>
                        <TableCell>
                          <Badge variant="secondary">{TYPE_LABEL[m.entityType]}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{m.label}</div>
                          {m.sublabel && (
                            <div className="text-xs text-muted-foreground">{m.sublabel}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {m.note || '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {fmtDate(m.markedAt)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Unmark"
                            onClick={() =>
                              unmarkMut.mutate({ entityType: m.entityType, entityId: m.entityId })
                            }
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Cleanup */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cleanup</CardTitle>
              <CardDescription>
                Preview the exact rows a cleanup would archive and delete. Resolve any
                conflicts before running.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="destructive"
                  onClick={() => setConfirmCleanup(true)}
                  disabled={!canRunCleanup}
                >
                  <Trash2 className="h-4 w-4" /> Run cleanup
                  {preview && canRunCleanup ? ` (${preview.totalRows})` : ''}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => previewQuery.refetch()}
                  disabled={previewQuery.isFetching || marked.length === 0}
                >
                  {previewQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Refresh
                </Button>
                {cleanupBlockedReason && (
                  <span className="text-[12px] text-muted-foreground">{cleanupBlockedReason}</span>
                )}
              </div>

              {preview && (
                <div className="space-y-3">
                  {preview.conflicts.length > 0 && (
                    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                      <div className="mb-1 flex items-center gap-1.5 font-medium text-destructive">
                        <AlertTriangle className="h-4 w-4" /> Blocked by conflicts
                      </div>
                      <ul className="list-inside list-disc text-muted-foreground">
                        {preview.conflicts.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                      {preview.resolvableOrders.length > 0 && (
                        <div className="mt-3 space-y-2 border-t border-destructive/20 pt-2.5">
                          <div className="text-[12.5px] text-foreground">
                            These {preview.resolvableOrders.length} order(s) reference the test
                            item and aren&apos;t marked yet. Mark them as test to pull them (and
                            their dispatches / returns) into the cleanup:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {preview.resolvableOrders.map((o) => (
                              <span
                                key={o.id}
                                className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground ring-1 ring-border"
                              >
                                {o.orderNumber}
                              </span>
                            ))}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              resolveMut.mutate(preview.resolvableOrders.map((o) => o.id))
                            }
                            disabled={resolveMut.isPending}
                          >
                            {resolveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            Mark these {preview.resolvableOrders.length} order(s) as test
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="rounded-md border">
                    <div className="border-b bg-muted/30 px-3 py-2 text-sm font-medium">
                      {preview.totalRows} row(s) would be archived + deleted
                    </div>
                    {Object.keys(preview.counts).length > 0 && (
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 text-sm sm:grid-cols-3">
                        {Object.entries(preview.counts).map(([model, n]) => (
                          <div key={model} className="flex justify-between">
                            <span className="text-muted-foreground">{model}</span>
                            <span className="font-mono">{n}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── History ─────────────────────────────────────────────── */}
        <TabsContent value="history" className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cleanup runs</CardTitle>
              <CardDescription>
                Every cleanup is recorded and restorable from its archive.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {runsQuery.isLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
              ) : runs.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No cleanups have run yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Started</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Rows</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="w-28" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-sm">{fmtDate(r.startedAt)}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                          {r.error && (
                            <div className="mt-1 max-w-xs text-xs text-destructive">{r.error}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{r.totalRows}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.note || '—'}
                        </TableCell>
                        <TableCell>
                          {r.status === 'COMPLETED' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRestoreTarget(r)}
                            >
                              <RotateCcw className="h-3.5 w-3.5" /> Restore
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm cleanup */}
      <Dialog open={confirmCleanup} onOpenChange={setConfirmCleanup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run cleanup?</DialogTitle>
            <DialogDescription>
              This will archive and then delete{' '}
              <span className="font-semibold">{preview?.totalRows ?? 0} row(s)</span> across{' '}
              {preview ? Object.keys(preview.counts).length : 0} table(s), and clear the current
              marks. It is restorable from History afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label className="text-xs">
              Cleanup note <span className="text-destructive">*</span>
            </Label>
            <Textarea
              rows={2}
              value={cleanupNote}
              onChange={(e) => setCleanupNote(e.target.value)}
              placeholder="Why is this cleanup being run?"
            />
            <p className="text-[11px] text-muted-foreground">
              Required — recorded on the run in History for the audit trail.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCleanup(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => cleanupMut.mutate()}
              disabled={cleanupMut.isPending || cleanupNote.trim().length === 0}
            >
              {cleanupMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Archive + delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm restore */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore this cleanup?</DialogTitle>
            <DialogDescription>
              This re-inserts the {restoreTarget?.totalRows ?? 0} archived row(s) from the run on{' '}
              {fmtDate(restoreTarget?.startedAt)} back into the live tables.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => restoreTarget && restoreMut.mutate(restoreTarget.id)}
              disabled={restoreMut.isPending}
            >
              {restoreMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
